// Phase 25 iter-15 (lote C) — SILLEDA_ADMIN KYC review queue.
//
// Lists every UserRoleAssignment with at least one PENDING_REVIEW document,
// oldest first. Per row the admin sees:
//   • who: name + email + role (VET / TRANSPORTISTA)
//   • the declared free-text fields (vet_license #, plate, auth numbers)
//     so obvious garbage like "asdfasdf" can be rejected at a glance
//   • a link to open each pending doc (S3 URL — bucket is private to the
//     leyrochain IAM, the URL is the canonical S3 path)
//   • "Aprobar" / "Rexeitar" buttons per (assignment, doc_kind) pair
//
// Rejection requires a reason that the user will see in their KYC screen.
// After every verdict the queue reloads so the row drops out.
//
// Access is gated client-side on `primary_role === 'SILLEDA_ADMIN'` AND
// server-side via /admin/kyc/* `isAdmin` middleware.

import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';

import type { AdminKycPendingItem, KycDocKind } from '@leyrochain/shared';

import { listPendingKyc, verdictKyc } from '../api/adminKyc';
import { describeAxiosError } from '../api/errors';
import Spinner from '../components/ui/Spinner';
import { useAuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const DOC_LABEL: Record<KycDocKind, string> = {
  vet_license: 'Carné colexiado veterinario',
  transporter_auth: 'Autorización de transporte',
  vehicle_auth: 'Autorización do vehículo',
  competence_cert: 'Certificado de competencia',
};

interface RejectDraft {
  assignmentId: string;
  docKind: KycDocKind;
  reason: string;
}

function declaredValueFor(row: AdminKycPendingItem, kind: KycDocKind): string | null {
  switch (kind) {
    case 'vet_license':
      return row.vet_license ?? null;
    case 'transporter_auth':
      return row.transporter_auth_no ?? null;
    case 'vehicle_auth':
      return row.vehicle_auth_no ?? null;
    case 'competence_cert':
      return row.competence_cert_no ?? null;
    default:
      return null;
  }
}

export default function AdminKycReviewPage(): JSX.Element {
  const { user } = useAuthContext();
  const { addToast } = useToast();

  const [rows, setRows] = useState<AdminKycPendingItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // `${assignmentId}:${docKind}`
  const [reject, setReject] = useState<RejectDraft | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listPendingKyc();
      setRows(res.rows);
    } catch (e) {
      setError(describeAxiosError(e, 'Non se puido cargar a cola de revisión.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  if (!user) return <Spinner />;
  // B19 fix (Lote Admin-V1) — admin can be a secondary role. Multi-role
  // aware gate; matches the backend `isAdmin()` helper which already uses
  // `userHasRole(user, 'SILLEDA_ADMIN')`.
  if (!user.roles.includes('SILLEDA_ADMIN')) {
    return <Navigate to="/marketplace" replace />;
  }

  async function approve(assignmentId: string, docKind: KycDocKind): Promise<void> {
    const key = `${assignmentId}:${docKind}`;
    setBusy(key);
    try {
      await verdictKyc(assignmentId, { doc_kind: docKind, decision: 'VERIFIED' });
      addToast('Documento aprobado.', 'success');
      await reload();
    } catch (e) {
      addToast(describeAxiosError(e, 'Non se puido aprobar.'), 'error');
    } finally {
      setBusy(null);
    }
  }

  async function submitReject(): Promise<void> {
    if (!reject || reject.reason.trim().length === 0) return;
    const key = `${reject.assignmentId}:${reject.docKind}`;
    setBusy(key);
    try {
      await verdictKyc(reject.assignmentId, {
        doc_kind: reject.docKind,
        decision: 'REJECTED',
        reject_reason: reject.reason.trim(),
      });
      addToast('Documento rexeitado.', 'success');
      setReject(null);
      await reload();
    } catch (e) {
      addToast(describeAxiosError(e, 'Non se puido rexeitar.'), 'error');
    } finally {
      setBusy(null);
    }
  }

  return (
    <main className="max-w-5xl mx-auto px-6 py-10">
      <header className="mb-8">
        <h1 className="text-3xl font-extrabold text-dark">Revisión KYC</h1>
        <p className="mt-2 text-sm text-muted">
          Documentos pendentes de aprobación manual (veterinarios e transportistas).
        </p>
        <p className="mt-2 text-xs text-muted">
          <Link to="/marketplace/profile" className="text-rauxa underline">
            Volver ao perfil
          </Link>
        </p>
      </header>

      {loading ? (
        <Spinner />
      ) : error ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
          <button onClick={() => void reload()} className="ml-3 underline" type="button">
            Reintentar
          </button>
        </div>
      ) : rows && rows.length === 0 ? (
        <div className="rounded-md border border-border bg-surface p-6 text-sm text-muted text-center">
          Non hai documentos pendentes neste momento.
        </div>
      ) : (
        <ul className="space-y-6">
          {(rows ?? []).map((row) => (
            <li
              key={row.assignment_id}
              className="rounded-lg border border-border bg-surface p-5 shadow-sm"
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <div className="text-base font-semibold text-dark">{row.user.name}</div>
                  <div className="text-xs text-muted">
                    {row.user.email ?? '—'} · {row.role}
                  </div>
                </div>
                <div className="text-xs text-muted">
                  Solicitado:{' '}
                  {new Date(row.oldest_pending_at).toLocaleDateString('gl', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })}
                </div>
              </div>

              <ul className="space-y-3">
                {row.pending_docs.map((kind) => {
                  const key = `${row.assignment_id}:${kind}`;
                  const declared = declaredValueFor(row, kind);
                  const docUrl = row.doc_urls?.[kind] ?? null;
                  const isBusy = busy === key;
                  const isRejecting =
                    reject?.assignmentId === row.assignment_id && reject?.docKind === kind;
                  return (
                    <li key={kind} className="rounded-md border border-border/60 bg-background p-3">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="text-sm">
                          <div className="font-medium text-dark">{DOC_LABEL[kind]}</div>
                          {declared ? (
                            <div className="mt-0.5 text-xs text-muted">
                              Declarado: <span className="font-mono">{declared}</span>
                            </div>
                          ) : null}
                          {docUrl ? (
                            <a
                              href={docUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-1 inline-block text-xs text-rauxa underline"
                            >
                              Abrir documento ↗
                            </a>
                          ) : (
                            <span className="mt-1 inline-block text-xs text-amber-700">
                              Sen URL de documento.
                            </span>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            disabled={isBusy}
                            onClick={() => void approve(row.assignment_id, kind)}
                            className="px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-semibold disabled:opacity-50"
                          >
                            {isBusy && !isRejecting ? 'Aprobando…' : 'Aprobar'}
                          </button>
                          <button
                            type="button"
                            disabled={isBusy}
                            onClick={() =>
                              setReject({
                                assignmentId: row.assignment_id,
                                docKind: kind,
                                reason: '',
                              })
                            }
                            className="px-3 py-1.5 rounded-md border border-red-300 text-red-700 text-xs font-semibold disabled:opacity-50"
                          >
                            Rexeitar
                          </button>
                        </div>
                      </div>

                      {isRejecting ? (
                        <div className="mt-3 space-y-2">
                          <label className="block text-xs text-muted">
                            Motivo do rexeitamento (visible para o usuario):
                          </label>
                          <textarea
                            value={reject.reason}
                            onChange={(e) => setReject({ ...reject, reason: e.target.value })}
                            maxLength={500}
                            rows={2}
                            className="w-full rounded-md border border-border bg-white p-2 text-sm"
                            placeholder="Ex.: o documento non está legible ou o número de colexiado non coincide."
                          />
                          <div className="flex gap-2 justify-end">
                            <button
                              type="button"
                              onClick={() => setReject(null)}
                              className="px-3 py-1.5 rounded-md border border-border text-xs"
                            >
                              Cancelar
                            </button>
                            <button
                              type="button"
                              disabled={isBusy || reject.reason.trim().length === 0}
                              onClick={() => void submitReject()}
                              className="px-3 py-1.5 rounded-md bg-red-600 text-white text-xs font-semibold disabled:opacity-50"
                            >
                              {isBusy ? 'Rexeitando…' : 'Confirmar rexeitamento'}
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
